import { useRef, useState, useMemo } from "react";
import type { LandingCategory } from "@/api/landingContent";
import { Skeleton } from "@/components/ui";
import { runtimeEnv } from "@penn/web-common";

import pemserterImg from "@/assets/images/brand-logo/pemserter.png";
import profilImg from "@/assets/images/brand-logo/profil.png";
import profilFeederImg from "@/assets/images/brand-logo/ProfilFeeder.png";
import robotImg from "@/assets/images/brand-logo/Robot.png";
import robotCellImg from "@/assets/images/brand-logo/Robotcell (2).png";

const requiredBaseUrl = (name: string, value: string | undefined): string => {
  const normalized = value?.trim().replace(/\/+$/, "");
  if (!normalized) {
    throw new Error(
      `Frontend config missing — set ${name} for this deployment.`,
    );
  }
  return normalized;
};

const MEDIA_BASE_URL = requiredBaseUrl(
  "VITE_MEDIA_BASE_URL",
  runtimeEnv("VITE_MEDIA_BASE_URL"),
);
const PUBLIC_ASSETS_BASE_URL = requiredBaseUrl(
  "VITE_PUBLIC_ASSETS_BASE_URL",
  runtimeEnv("VITE_PUBLIC_ASSETS_BASE_URL"),
);

/** Resolve a media (video) URL. Relative keys like "media/Handtools.mp4" get
 * the CDN/S3 host from VITE_MEDIA_BASE_URL — the host is NEVER hardcoded. */
const resolveMediaUrl = (url: string): string => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  // Admin-uploaded hero videos live in the public-assets bucket (like landing
  // images), not the SPA/media bucket that serves the bundled fallback clips.
  if (url.startsWith("landing-videos/")) {
    return `${PUBLIC_ASSETS_BASE_URL}/${url.replace(/^\/+/, "")}`;
  }
  return `${MEDIA_BASE_URL}/${url.replace(/^\/+/, "")}`;
};

/** Known POC product-image relative paths → bundled local assets (these ship
 * with the app, not behind the API), so the detail panel matches the POC. */
const LOCAL_ASSET_MAP: Record<string, string> = {
  "/assets/images/robot.png": robotImg,
  "/assets/images/robotcell (2).png": robotCellImg,
  "/assets/images/robotcell%20(2).png": robotCellImg,
};

/** Resolve a detail image URL. Absolute → as-is; a known POC relative asset →
 * bundled local asset; otherwise prepend the media base. */
const resolveImageUrl = (url: string): string => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const mapped = LOCAL_ASSET_MAP[url.toLowerCase()];
  if (mapped) return mapped;
  // Uploaded landing images live in the public-assets bucket, not the SPA/media bucket.
  return `${PUBLIC_ASSETS_BASE_URL}/${url.replace(/^\/+/, "")}`;
};

type ImageItem = {
  type: string;
  sortOrder: number;
  productId: string | null;
  imageUrl: string;
  caption: string;
};

const mkImage = (imageUrl: string, caption = ""): ImageItem => ({
  type: "",
  sortOrder: 0,
  productId: null,
  imageUrl,
  caption,
});

type Category = {
  id: string;
  title: string;
  subtitle: string;
  img: string;
  video: string;
  detail: {
    tagline: string;
    description: string[];
    bullets: string[];
    images: ImageItem[];
  };
};

/** Fallback video relative key per category key — used only when the API row
 * has no video_url yet. Final host comes from VITE_MEDIA_BASE_URL. */
const CATEGORY_VIDEOS: Record<string, string> = {
  Custom: "/media/custom.mp4",
  Handtools: "/media/Handtools.mp4",
  InDie: "/media/InDie.mp4",
  "Robotic Integration": "/media/robotic.mp4",
  "Standalone Machines": "/media/Standalone.mp4",
};

/** Fallback image per category key (used when API image URL is relative or unavailable). */
const CATEGORY_FALLBACK_IMGS: Record<string, string> = {
  Custom: robotCellImg,
  Handtools: pemserterImg,
  InDie: profilImg,
  "Robotic Integration": robotImg,
  "Standalone Machines": profilFeederImg,
};

const POC_ORDER = ["handtools", "standalone", "robotic", "indie", "custom"];
const pocOrderRank = (key: string): number => {
  const k = key.toLowerCase();
  const idx = POC_ORDER.findIndex((p) => k.startsWith(p));
  return idx === -1 ? POC_ORDER.length : idx;
};

/** Map one API category to the internal Category shape. */
const fromApiCategory = (api: LandingCategory): Category => {
  const sorted = <T extends { sortOrder: number }>(arr: T[]) =>
    [...arr].sort((a, b) => a.sortOrder - b.sortOrder);

  const paras = sorted(api.paragraphs).map((p) => p.paragraphText);
  const bullets = sorted(api.bullets).map((b) => b.bulletText);
  const images: ImageItem[] = sorted(api.images).map((i) => ({
    type: i.categoryKey,
    sortOrder: i.sortOrder,
    productId: i.productId,
    imageUrl: resolveImageUrl(i.imageUrl),
    caption: i.caption,
  }));

  // Prefer the DB video_url; fall back to the bundled per-category clip. Either
  // way the host is supplied by VITE_MEDIA_BASE_URL (never hardcoded).
  const videoKey = api.videoUrl ?? CATEGORY_VIDEOS[api.categoryKey] ?? "";
  const fallbackImg = CATEGORY_FALLBACK_IMGS[api.categoryKey] ?? pemserterImg;

  return {
    id: api.categoryKey,
    title: api.title,
    subtitle: api.subtitle,
    img: images[0]?.imageUrl ?? fallbackImg,
    video: resolveMediaUrl(videoKey),
    detail: {
      tagline: paras[0] ?? "",
      description: paras.slice(1),
      bullets,
      images: images.length > 0 ? images : [mkImage(fallbackImg)],
    },
  };
};

export const HeroSection = ({
  apiCategories,
  isLoading,
}: {
  apiCategories?: LandingCategory[];
  isLoading?: boolean;
}) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const resolvedCategories = useMemo<Category[]>(() => {
    if (!apiCategories) return [];
    return [...apiCategories]
      .sort((a, b) => pocOrderRank(a.categoryKey) - pocOrderRank(b.categoryKey))
      .map(fromApiCategory);
  }, [apiCategories]);

  const handleClick = (id: string) =>
    setOpenId((prev) => (prev === id ? null : id));

  if (isLoading && resolvedCategories.length === 0) {
    return (
      <section className="w-full flex-1 min-h-0 flex flex-col">
        <div className="relative py-4 sm:py-5 flex-1 min-h-0 w-full flex flex-col md:flex-row px-3 sm:px-6 gap-3">
          {Array.from({ length: POC_ORDER.length }).map((_, i) => (
            <Skeleton
              key={i}
              className="basis-0 grow h-full w-full"
              rounded="lg"
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="w-full flex-1 min-h-0 flex flex-col">
      {/* Panel grid — flex-1 fills whatever viewport height remains under the
          fixed header/brand bar (no leftover page space below the tiles). */}
      <div className="relative py-4 sm:py-5 flex-1 min-h-0 w-full flex flex-col md:flex-row px-3 sm:px-6 gap-x-3">
        {resolvedCategories.map((cat) => (
          <LandingPanel
            key={cat.id}
            category={cat}
            isOpen={openId === cat.id}
            anyOpen={openId !== null}
            onClick={() => handleClick(cat.id)}
          />
        ))}
      </div>
    </section>
  );
};

/* ── Panel ── */
type LandingPanelProps = {
  category: Category;
  isOpen: boolean;
  anyOpen: boolean;
  onClick: () => void;
};

const LandingPanel = ({
  category,
  isOpen,
  anyOpen,
  onClick,
}: LandingPanelProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { title, subtitle, img, video, detail } = category;

  const handleMouseEnter = () => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    const v = videoRef.current;
    if (v && !isOpen) {
      v.pause();
      v.currentTime = 0;
    }
  };

  const collapsed = anyOpen && !isOpen;

  // Single element whose flex sizing transitions, so the clicked tile grows in
  // place and neighbors compress on their own side (book-opening), instead of
  // swapping render branches that snap with no transition.
  const sizing = isOpen
    ? "grow-[20] min-h-0"
    : collapsed
      ? "grow-0 min-h-10 md:min-h-0 md:min-w-14 lg:min-w-16"
      : "grow min-h-0";

  return (
    <div
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group relative basis-0 overflow-hidden cursor-pointer border-b md:border-b-0 md:border-r border-white/10 last:border-0 transition-all duration-500 ease-in-out ${sizing}`}
    >
      {/* Video layer */}
      <video
        ref={videoRef}
        src={video}
        muted
        loop
        playsInline
        preload="metadata"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 ${
          isOpen
            ? "grayscale-0 opacity-80"
            : "grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-80"
        }`}
      />
      {/* Fallback image while video loads */}
      <img
        src={img}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover -z-10 grayscale opacity-60"
      />

      {/* Gradient */}
      <div className="absolute inset-0 bg-linear-to-t from-white/70 via-white/20  to-black/10" />

      {/* Label — rotated strip title when collapsed, bottom label otherwise */}
      {collapsed ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <span
            className="text-3xl font-semibold whitespace-nowrap hidden md:block text-black"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {title}
          </span>
          <span className="text-caption text-black md:hidden">
            {title}
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-end p-5 z-10 justify-center">
          <div>
            <div className="text-h3 md:text-h2 text-black text-center">
              {title}
            </div>
            <div className="text-body hidden md:block text-black text-center">
              {subtitle}
            </div>
          </div>
        </div>
      )}

      {/* Detail overlay (when open) */}
      {isOpen && (
        <div className="absolute inset-x-4 inset-y-4 md:inset-x-8 md:inset-y-6 z-20 bg-white backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden flex flex-col">
          <div className="p-6 md:p-8 flex flex-col flex-1 min-h-0">
            {/* Header */}
            <div className="text-center mb-5">
              <h2 className="text-h3 text-penn-900">
                {title}
              </h2>
              <p className="text-body text-blue_grey2 mt-1">{subtitle}</p>
            </div>

            {/* Body */}
            <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0 overflow-y-auto">
              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-gray-800 mb-3 leading-snug">
                  {detail.tagline}
                </p>
                {detail.description.map((para, i) => (
                  <p
                    key={i}
                    className="text-[15px] text-gray-600 mb-2.5 leading-relaxed"
                  >
                    {para}
                  </p>
                ))}
                <ul className="mt-3 space-y-1.5">
                  {detail.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[15px] text-gray-600"
                    >
                      <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-gray-700" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Images — stacked vertically so the text column stays wide
                  enough to avoid an internal scrollbar. */}
              <div className="flex flex-row flex-wrap gap-4 shrink-0 items-start justify-center lg:justify-start">
                {detail.images.map((image, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5">
                    <div className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 xl:w-70 xl:h-70 rounded-2xl bg-white flex items-center justify-center overflow-hidden">
                      <img
                        src={image.imageUrl}
                        alt={image.caption || `${title} ${i + 1}`}
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    {image.caption && (
                      <span className="text-[13px] text-gray-500 text-center max-w-40 sm:max-w-48 lg:max-w-56">
                        {image.caption}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
